
export interface Assessment {
    id: string;
    title: string;
    contentVersion: string;
    hsim: string;
    customGuideline: boolean;
    customGuidelineDisclaimer?: string;
    sectionsSet: Section[];
}

export interface Section {
    id: string;
    uid: string;
    heading: string;
    sortOrder: number;
    sectionsSet: SubSection[];
}

export interface SubSection {
    id: string;
    uid: string;
    heading: string;
    sortOrder: number;
    questionsSet: Question[];
    problemsSet: Problem[];
    barriersSet: Barrier[];
}

export interface QuestionReference {
    id: string;
}

export interface Question extends IAnnotatable {
    id: string;
    complete?: boolean;
    enabled?: boolean;
    required?: boolean;
    affectsSubSectionState?: boolean;
    type: QuestionType;
    inputType: string;
    facet?: QuestionFacet;
    questionText: string;
    response?: string;
    displayValue: string;
    sortOrder: number;
    topLevel?: boolean;
    voiceDisplayValue: string;
    answersSet: Choice[];
    questionReferencesSet: QuestionReference[];
}

export interface IQuestionConfig {
    question: Question;
    questionNumberDisplay: string;
    readonly: boolean;
    existingAttachments: string[];
    labelId: string;
}

export interface IQuestionComponent {
    question: Question | null;
    isValid: boolean;

    getAnswers: (() => Choice[]) | null;
    getGuidelineLinks: (() => GuidelineLink[]) | null;
    clearSelectedAnswers: (() => void) | null;
}

export enum QuestionType {
    singleSelect = 1,
    multiSelect = 2,
    text = 3,
    date = 4,
    numeric = 5
}

export enum QuestionFacet {
    Radio = 1,
    Checkbox = 2
}

export interface Choice extends IAnnotatable {
    id: string;
    enabled?: boolean;
    affectsSubSectionState?: boolean;
    mutuallyExclusive?: boolean;
    response?: boolean;
    text: string;
    value: string;
    selected?: boolean;
    sortOrder?: number;
    inputType?: string;
    uid: string;
    guidelineLinkSet: GuidelineLink[];
    annotationSet: IAnnotation[];
    citationSet: ICitation[];
    footnoteSet: IFootnote[];
    questionRefSet: QuestionReference[];
}

export interface GuidelineLink {
    id: string;
    hsim: string;
    uid: string;
    contentVersion: string;
    title: string;
}

export interface GuidelineLinks {
    guidelineLinkSet: GuidelineLink[];
}

export interface Guideline {
    hsim: string;
    guidelineTitle: string;
    edition: string;

    Hsim: string;
    Edition: string;
}

export interface Problem {
    id: string;
    uid: string;
    identified: boolean;
    text: string;
    goalsSet: IGoal[];
}

export interface Barrier {
    uid: string;
    identified: boolean;
    text: string;
}

export interface IAnnotatable {
    id: string;
    text: string;
    uid: string;
    sequence: number | null;
    annotationSet: IAnnotation[];
    citationSet: ICitation[];
    footnoteSet: IFootnote[];
    subinterventionsSet: IIntervention[] | null;
}

export interface IGoal extends IAnnotatable {
    interventionsSet: IIntervention[];
    sanitizedText: string | null;
    $expanded: boolean;
}

export interface IIntervention extends IAnnotatable {
    type: string;  //In a plan of care, specifies the type of intervention to be taken, such as "educate" or "send."
}

export interface IFootnote extends IAnnotatable { }

export interface ICitation extends IAnnotatable { }

export interface IAnnotation extends IAnnotatable {
    type: string;  //In an annotation, refers to the type of evidence, such as EVI (Evidence), CRI (Criteria), ALT (Alternative), LOS (Length of Stay), and HOS (Hospitalization).
}

export interface IPlanOfCareConfig {
    assessmentAttachmentId: string;
    readonly: boolean;
}

export interface QualityMeasure {
    heading: string;
    content: QualityMeasureContent[];
}

export interface QualityMeasureContent {
    text: string;
    id: string;
    citationLinks: CitationLink[];
    contentItems: QualityMeasureContentItem[];
}

export interface QualityMeasureContentItem {
    text: string;
    id: string;
}

export interface CitationLink {
    id: string;
    displayValue: string;
    link: string;
    citationText: string;
    citationNumber: string;
    citationLabelText: string;
}
